import moment from 'moment'
import type { SpendChartView } from 'src/queries/reporting/reporting.models'

import { firstPresentMetadataValue } from './reportingChartData'

export const ISO_REPORTING_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ISO_MONTH_PATTERN = /^\d{4}-\d{2}$/
const ISO_WEEK_PATTERN = /^\d{4}-W\d{2}$/

export const formatPeriodLabel = (raw: string): string => {
  if (!raw) return raw

  if (ISO_MONTH_PATTERN.test(raw)) {
    const month = moment.utc(raw, 'YYYY-MM', true)
    return month.isValid() ? month.format('MMM') : raw
  }
  if (ISO_REPORTING_DAY_PATTERN.test(raw)) {
    const day = moment.utc(raw, 'YYYY-MM-DD', true)
    return day.isValid() ? day.format('MMM D') : raw
  }
  if (ISO_WEEK_PATTERN.test(raw)) {
    const week = moment.utc(raw, 'GGGG-[W]WW', true)
    return week.isValid() ? week.format('MMM D') : raw
  }
  return raw
}

export type ReportingPeriodUnit = 'day' | 'week' | 'month'

export const detectPeriodUnit = (
  isoBuckets: string[],
): ReportingPeriodUnit | null => {
  const unique = Array.from(new Set(isoBuckets)).sort()
  if (unique.length === 0) return null

  const allFirstOfMonth = unique.every(
    (bucket) => moment.utc(bucket, 'YYYY-MM-DD', true).date() === 1,
  )
  if (allFirstOfMonth) return 'month'
  if (unique.length < 2) return 'day'

  let minGapDays = Infinity
  for (let index = 1; index < unique.length; index += 1) {
    const gap = moment
      .utc(unique[index], 'YYYY-MM-DD', true)
      .diff(moment.utc(unique[index - 1], 'YYYY-MM-DD', true), 'days')
    if (gap > 0) minGapDays = Math.min(minGapDays, gap)
  }
  if (!Number.isFinite(minGapDays)) return 'day'
  if (minGapDays >= 28) return 'month'
  if (minGapDays >= 5) return 'week'
  return 'day'
}

export const formatPeriodLabelForUnit = (
  raw: string,
  unit: ReportingPeriodUnit,
): string => {
  if (unit === 'month' && ISO_REPORTING_DAY_PATTERN.test(raw)) {
    const month = moment.utc(raw, 'YYYY-MM-DD', true)
    if (month.isValid()) return month.format('MMM')
  }
  return formatPeriodLabel(raw)
}

export const buildPeriodDomain = (
  chart: SpendChartView,
): string[] | undefined => {
  const hasExplicitLabels = chart.points.some(
    (point) =>
      firstPresentMetadataValue(point, ['bucketLabel', 'periodLabel']) !==
      undefined,
  )
  if (hasExplicitLabels) return undefined

  const isoBuckets = chart.points
    .map((point) => point.bucket)
    .filter(
      (bucket): bucket is string =>
        typeof bucket === 'string' && ISO_REPORTING_DAY_PATTERN.test(bucket),
    )
  if (isoBuckets.length === 0) return undefined

  const since = moment.utc(chart.since, 'YYYY-MM-DD', true)
  const until = moment.utc(chart.until, 'YYYY-MM-DD', true)
  if (!since.isValid() || !until.isValid() || until.isBefore(since)) {
    return undefined
  }

  const unique = Array.from(new Set(isoBuckets)).sort()
  const anchor = moment.utc(unique[0], 'YYYY-MM-DD', true)
  const unit: moment.unitOfTime.DurationConstructor =
    detectPeriodUnit(isoBuckets) ?? 'day'
  const rangeStart = unit === 'month' ? since.clone().startOf('month') : since
  const cursor = anchor.clone()
  let guard = 0
  while (cursor.isAfter(rangeStart) && guard < 5000) {
    cursor.subtract(1, unit)
    guard += 1
  }

  const domain: string[] = []
  guard = 0
  while (cursor.isSameOrBefore(until) && guard < 5000) {
    domain.push(cursor.format('YYYY-MM-DD'))
    cursor.add(1, unit)
    guard += 1
  }
  return domain.length > 0 ? domain : undefined
}

export const MAX_CHART_COLUMNS = 8

const formatPeriodRange = (members: string[]): string => {
  if (members.length <= 1) return formatPeriodLabel(members[0] ?? '')

  const start = moment.utc(members[0], 'YYYY-MM-DD', true)
  const end = moment.utc(members[members.length - 1], 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return formatPeriodLabel(members[0])

  const monthly = members.every(
    (member) => moment.utc(member, 'YYYY-MM-DD', true).date() === 1,
  )
  if (monthly) {
    return start.isSame(end, 'month')
      ? start.format('MMM')
      : `${start.format('MMM')} – ${end.format('MMM')}`
  }
  return start.isSame(end, 'month')
    ? `${start.format('MMM D')} – ${end.format('D')}`
    : `${start.format('MMM D')} – ${end.format('MMM D')}`
}

export const capPeriodColumns = (
  domain: string[],
): { domain: string[]; columnForBucket: (bucket: string) => string } => {
  if (domain.length <= MAX_CHART_COLUMNS) {
    return { domain, columnForBucket: (bucket) => bucket }
  }

  const size = Math.ceil(domain.length / MAX_CHART_COLUMNS)
  const columns: string[] = []
  const columnByBucket = new Map<string, string>()
  for (let start = 0; start < domain.length; start += size) {
    const members = domain.slice(start, start + size)
    const label = formatPeriodRange(members)
    columns.push(label)
    members.forEach((member) => columnByBucket.set(member, label))
  }
  return {
    domain: columns,
    columnForBucket: (bucket) => columnByBucket.get(bucket) ?? bucket,
  }
}
